// Shared presentation for season standings: the row types, the points-gap
// decorator, formatters, and the Podium graphic. Used by both the manager
// race-control standings page and the public results page so the two stay
// visually identical.

import { createMemo, Show, For } from "solid-js";
import html from "solid-js/html";
import { Icon } from "../ui/Icons.ts";

// Re-exported so callers can render the position cell + bold the names of the
// top three in their standings tables.
export { Icon };

export interface ChampionshipStatistics {
    tiebreak_key: string;
    wins: number;
    finish_counts: { position: number; count: number }[];
    recent_results: {
        session_id: string;
        event_id: string;
        event_name: string;
        sequence_time: string;
        session_order: number;
        finish_position: number | null;
    }[];
}

export interface DriverStanding {
    championship?: ChampionshipStatistics;
    position: number;
    driver_id: string;
    first_name: string;
    last_name: string;
    full_name: string;
    car_number?: string;
    points: number;
    // Championship position supplied by the server, including tiebreakers.
    rank: number;
    is_leader: boolean;
    points_differential: number;
    points_from_first: number;
}

export interface CarStanding {
    championship?: ChampionshipStatistics;
    position: number;
    car_id: string;
    number: string;
    owner_name: string;
    class_id: string;
    class_name: string;
    points: number;
    // Championship position supplied by the server, including tiebreakers.
    rank: number;
    is_leader: boolean;
    points_differential: number;
    points_from_first: number;
}

export type StandingItem = DriverStanding | CarStanding;

export type StandingsType = "driver" | "car";

export const TYPE_DRIVER: StandingsType = "driver";
export const TYPE_CAR: StandingsType = "car";
export const TYPES: StandingsType[] = [TYPE_DRIVER, TYPE_CAR];

const numberFmt = new Intl.NumberFormat("en-US");

export function formatPoints(n: number): string {
    return numberFmt.format(n || 0);
}

export function formatDriverCarNumbers(numbers?: string): string {
    return (numbers || "").split(",").map(number => number.trim()).filter(Boolean).map(number => `#${number}`).join(", ");
}

export function DriverCarNumbers(props: { numbers?: string }) {
    return html`<${Show} when=${() => props.numbers}>
        <div class="text-xs text-neutral-500 font-normal">${() => formatDriverCarNumbers(props.numbers)}</div>
    <//>`;
}

export function finishCount(stats: ChampionshipStatistics | undefined, position: number): number {
    return stats?.finish_counts.find((finish) => finish.position === position)?.count ?? 0;
}

export function finishLabel(position: number): string {
    const lastTwo = position % 100;
    const suffix = lastTwo >= 11 && lastTwo <= 13 ? "th"
        : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[position % 10] || "th";
    return `${position}${suffix}`;
}

// The complete feature record keeps lower-place countbacks and the recent-race
// comparison visible as statistics, without generating prose explanations.
export function ChampionshipDetails(props: { stats?: ChampionshipStatistics }) {
    const results = () => props.stats?.recent_results || [];
    const counts = () => props.stats?.finish_counts || [];
    const raceLabel = (race: ChampionshipStatistics["recent_results"][number]) =>
        `${race.event_name} · ${new Date(race.sequence_time).toLocaleDateString()}`;
    return html`<details class="text-xs min-w-28">
        <summary class="cursor-pointer text-primary font-medium">Feature record</summary>
        <div class="mt-3 min-w-60 max-w-sm space-y-3">
            <div>
                <div class="font-semibold text-text-heading mb-1">Finish counts</div>
                <${Show} when=${() => counts().length > 0} fallback=${html`<span class="text-neutral-500">No recorded finishes</span>`}>
                    <dl class="grid grid-cols-2 gap-x-4 gap-y-1">
                        <${For} each=${counts}>${(finish: { position: number; count: number }) => html`<>
                            <dt>${finishLabel(finish.position)}</dt><dd class="text-right tabular-nums">${finish.count}</dd>
                        </>`}<//>
                    </dl>
                <//>
            </div>
            <${Show} when=${() => results().length > 0}>
                <div>
                    <div class="font-semibold text-text-heading mb-1">Recent features · newest first</div>
                    <dl class="space-y-1">
                        <${For} each=${results}>${(race: ChampionshipStatistics["recent_results"][number]) => html`
                            <div class="flex items-baseline justify-between gap-4">
                                <dt class="text-neutral-500" title=${new Date(race.sequence_time).toLocaleString()}>${raceLabel(race)}</dt>
                                <dd class="tabular-nums whitespace-nowrap">${race.finish_position === null ? "No finish" : finishLabel(race.finish_position)}</dd>
                            </div>`}<//>
                    </dl>
                </div>
            <//>
        </div>
    </details>`;
}

// Only the championship leader has nobody ahead. The management points view
// can have shared leaders; equal points after a tiebreaker display a gap of zero.
export function formatGap(isLeader: boolean, value: number): string {
    if (isLeader) return "—";
    return numberFmt.format(value || 0);
}

// Championship views preserve the server's unique positions. The management
// points view retains shared ranks and gaps to the next higher points total.
export function withGaps<T extends { points: number; position: number }>(rows: T[], ranking: "championship" | "points" = "championship"): T[] {
    if (!rows || rows.length === 0) return [];
    if (ranking === "points") {
        const ordered = rows.map(r => r.points || 0).sort((a, b) => b - a);
        const totals = new Map<number, { rank: number; differential: number }>();
        for (let i = 0; i < ordered.length; i++) {
            if (!totals.has(ordered[i])) {
                totals.set(ordered[i], { rank: i + 1, differential: i === 0 ? 0 : ordered[i - 1] - ordered[i] });
            }
        }
        return rows.map(r => {
            const points = r.points || 0;
            const total = totals.get(points)!;
            return { ...r, rank: total.rank, is_leader: total.rank === 1,
                points_differential: total.differential, points_from_first: ordered[0] - points };
        });
    }
    const byPosition = new Map(rows.map((r) => [r.position, r.points || 0]));
    const leaderPoints = byPosition.get(1) ?? 0;
    return rows.map((r) => {
        const p = r.points || 0;
        return {
            ...r,
            rank: r.position,
            is_leader: r.position === 1,
            points_differential: (byPosition.get(r.position - 1) ?? p) - p,
            points_from_first: leaderPoints - p,
        };
    });
}

// The trophy/medal shown beside the top three positions in a standings table:
// gold trophy for 1st, silver medal for 2nd, bronze medal for 3rd. Returns null
// for every other rank so the cell falls back to the plain position number.
export interface RankMedal {
    icon: string;
    colorClass: string;
}

export function rankMedal(rank: number): RankMedal | null {
    if (rank === 1) return { icon: "trophy", colorClass: "text-yellow-500" };
    if (rank === 2) return { icon: "medal", colorClass: "text-neutral-400" };
    if (rank === 3) return { icon: "medal", colorClass: "text-amber-700" };
    return null;
}

// Tailwind classes for a competitor's name: bold for the top three, normal
// otherwise. Pair with RankCell so the table reads as a clear leaderboard.
export function rankNameClass(rank: number): string {
    return rank <= 3 ? "font-bold text-text-heading" : "";
}

// The contents of a standings table's position cell: the medal/trophy icon
// (top three) followed by the position number, or just the number otherwise.
export function RankCell(props: { rank: number }) {
    const medal = rankMedal(props.rank);
    return html`<span class="inline-flex items-center justify-center gap-1.5">
        ${medal ? html`<${Icon} icon=${medal.icon} size=${14} class=${medal.colorClass} />` : ""}
        <span class="tabular-nums">${props.rank}</span>
    </span>`;
}

function podiumLabel(item: StandingItem | null, type: StandingsType): string {
    if (!item) return "";
    return type === TYPE_DRIVER
        ? (item as DriverStanding).full_name
        : `#${(item as CarStanding).number} - ${(item as CarStanding).owner_name}`;
}

function podiumSubLabel(item: StandingItem | null, type: StandingsType): string {
    if (!item) return "";
    return type === TYPE_DRIVER
        ? formatDriverCarNumbers((item as DriverStanding).car_number) || "Driver"
        : (item as CarStanding).class_name;
}

// Chamfered ("clipped") corners on the top-left + bottom-right, matching the
// cut-corner card treatment used elsewhere in the app (see ui/Cards.ts). The
// card uses two stacked layers: the coloured outer clip shows through a 2px
// inset as the border, the white inner clip is the face.
// calc() needs real spaces around the "-"; the compiler doesn't add them, so
// we force them with "_-_" (underscores compile to spaces in arbitrary values).
const CLIP_STEP = "[clip-path:polygon(12px_0,100%_0,100%_calc(100%_-_12px),calc(100%_-_12px)_100%,0_100%,0_12px)]";
const CLIP_CARD_OUTER = "before:[clip-path:polygon(12px_0,100%_0,100%_calc(100%_-_12px),calc(100%_-_12px)_100%,0_100%,0_12px)]";
const CLIP_CARD_INNER = "after:[clip-path:polygon(10px_0,100%_0,100%_calc(100%_-_10px),calc(100%_-_10px)_100%,0_100%,0_10px)]";

// Scaffolding for a chamfered competitor card: an outer coloured clip layer
// (the border) behind a white inner clip layer inset by 2px (the face). The
// per-rank before:bg-* colour and drop-shadow are appended by the caller.
const PODIUM_CARD_BASE =
    "relative isolate w-full max-w-[11rem] text-center " +
    "before:content-[''] before:absolute before:inset-0 before:-z-20 " + CLIP_CARD_OUTER + " " +
    "after:content-[''] after:absolute after:inset-[2px] after:bg-white after:-z-10 " + CLIP_CARD_INNER;

interface RankStyle {
    stepHeight: string;
    stepClass: string;
    stepTextSize: string;
    // before:bg-* colour of the outer clip layer that reads as the card border.
    cardBorderClass: string;
    cardShadowClass: string;
    badgeClass: string;
    accentClass: string;
    medalIcon: string;
    iconSize: number;
}

// Visual treatment keyed off championship position.
function rankStyle(rank: number): RankStyle {
    if (rank <= 1) {
        return {
            stepHeight: "h-24 sm:h-28",
            stepClass: "bg-linear-to-b from-primary to-primary-dark",
            stepTextSize: "text-3xl",
            cardBorderClass: "before:bg-primary",
            cardShadowClass: "drop-shadow-md",
            badgeClass: "bg-primary text-white",
            accentClass: "text-primary",
            medalIcon: "trophy",
            iconSize: 22,
        };
    }
    if (rank === 2) {
        return {
            stepHeight: "h-16 sm:h-20",
            stepClass: "bg-linear-to-b from-neutral-700 to-neutral-900",
            stepTextSize: "text-2xl",
            cardBorderClass: "before:bg-neutral-900",
            cardShadowClass: "",
            badgeClass: "bg-neutral-900 text-white",
            accentClass: "text-neutral-800",
            medalIcon: "medal",
            iconSize: 18,
        };
    }
    return {
        stepHeight: "h-12 sm:h-14",
        stepClass: "bg-linear-to-b from-neutral-400 to-neutral-500",
        stepTextSize: "text-2xl",
        cardBorderClass: "before:bg-neutral-400",
        cardShadowClass: "",
        badgeClass: "bg-neutral-400 text-white",
        accentClass: "text-neutral-600",
        medalIcon: "medal",
        iconSize: 18,
    };
}

interface PodiumTier {
    // The medal place this column represents (2nd left, 1st centre, 3rd right).
    // Doubles as the number shown on the step.
    place: 1 | 2 | 3;
    items: StandingItem[];
}

interface PodiumProps {
    items: StandingItem[];
    type: StandingsType;
}

export function Podium(props: PodiumProps) {
    // Each unique championship position has one podium tier.
    const tiers = createMemo<PodiumTier[]>(() => {
        const items = props.items || [];
        const atRank = (rank: number) => items.filter((it) => it.rank === rank);
        // 2nd left, 1st centre, 3rd right - canonical podium arrangement.
        return [
            { place: 2, items: atRank(2) },
            { place: 1, items: atRank(1) },
            { place: 3, items: atRank(3) },
        ];
    });

    // One competitor's chamfered card, coloured by its tier's rank style.
    const card = (item: StandingItem, style: RankStyle) => html`
        <div class=${PODIUM_CARD_BASE + " " + style.cardBorderClass + " " + style.cardShadowClass}>
            <div class="px-3 py-3">
                <div class=${"mx-auto flex h-9 w-9 items-center justify-center " + style.badgeClass}>
                    <${Icon} icon=${style.medalIcon} size=${style.iconSize} />
                </div>
                <div class="font-bold text-sm text-text-heading mt-2 line-clamp-2 leading-tight">
                    ${() => podiumLabel(item, props.type)}
                </div>
                <div class="text-2xs uppercase text-neutral-500 mt-0.5">
                    ${() => podiumSubLabel(item, props.type)}
                </div>
                <div class="mt-2 flex items-baseline justify-center gap-1">
                    <span class=${"font-display font-extrabold text-xl sm:text-2xl tabular-nums " + style.accentClass}>
                        ${() => formatPoints(item.points)}
                    </span>
                    <span class="text-2xs uppercase text-neutral-500">pts</span>
                </div>
            </div>
        </div>`;

    return html`<div class="rounded-default border border-neutral-300 bg-white p-4 sm:p-5 shadow-sm mb-6">
        <div class="flex items-center gap-3 mb-4">
            <span class="text-2xs uppercase text-neutral-500 font-medium">Podium</span>
            <div class="h-px flex-1 bg-neutral-200"></div>
        </div>

        <div class="grid grid-cols-3 items-end gap-2 sm:gap-3 max-w-2xl mx-auto">
            <${For} each=${tiers}>${(tier: PodiumTier) => {
                const style = rankStyle(tier.place);
                return html`
                <div class="flex flex-col items-center gap-2">
                    <${Show}
                        when=${() => tier.items.length > 0}
                        fallback=${() => html`
                            <div class=${"w-full max-w-[11rem] border border-dashed border-neutral-300 bg-neutral-50 px-3 py-3 text-center text-neutral-400 " + CLIP_STEP}>
                                <div class="text-2xs uppercase">No competitor</div>
                                <div class="text-xs mt-1">-</div>
                            </div>
                            <div class=${"w-full max-w-[11rem] flex items-center justify-center text-white font-display font-extrabold " + CLIP_STEP + " " + style.stepHeight + " " + style.stepTextSize + " " + style.stepClass}>
                                ${tier.place}
                            </div>
                        `}
                    >
                        <${For} each=${() => tier.items}>${(item: StandingItem) => card(item, style)}<//>
                        <div class=${"w-full max-w-[11rem] flex items-center justify-center text-white font-display font-extrabold drop-shadow-sm " + CLIP_STEP + " " + style.stepHeight + " " + style.stepTextSize + " " + style.stepClass}>
                            ${tier.place}
                        </div>
                    <//>
                </div>
            `;
            }}<//>
        </div>
    </div>`;
}
